/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  Group,
  Text,
  Table,
  ScrollArea,
  Badge,
  Pagination,
  createStyles,
  Center,
  Avatar,
  Flex,
  Select,
  Divider,
  Loader,
  Tooltip,
  Button,
  Box,
  Modal,
  Image,
  Grid,
  ActionIcon,
  TextInput,
  SegmentedControl,
} from "@mantine/core";
import { useGetAttendanceAdminList } from "api/attendance/getAttendanceAdminList";
import { useGetPrograms } from "api/program/getprogram";
import { formatDateToDDMMYYYY, formatToLocalTime } from "utils/dateFormate";
import DateRangeSelector, { DateRange } from "utils/customDateRangeSelector";
import UnclosedSessionModal from "components/session/unCloseSession/uncloseSession";
import { useGetUnclosedSessions } from "api/attendance/unCloseSession";
import { useUpdateOvertimeStatus } from "api/attendance/approveOvertime";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useUser } from "hooks/useUser";
import AdminAttendanceDetail from "./AdminAttendanceDetail";
import { IconPhoto, IconTrash, IconPencil } from "@tabler/icons-react";
import DeleteAttendanceModal from "./DeleteAttendanceModal";
import EditAttendanceModal from "./EditAttendanceModal";

type RangeOption =
  | "Today"
  | "Yesterday"
  | "Last 7 Days"
  | "Last 30 Days"
  | "This Month"
  | "Last Month"
  | "Custom Range";

const useStyles = createStyles((theme) => ({
  containerCard: {
    padding: "18px",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[3]}`,
    boxShadow: "none",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "16px",
  },
  titleLeft: {
    fontWeight: 600,
    color: theme.colors.dark[6],
  },
  filtersContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  filterWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "68px", // Fixed height for all filter wrappers
    justifyContent: "flex-end",
  },
  filterLabel: {
    marginBottom: "4px",
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
  },
  tableHeader: {
    backgroundColor:
      theme.colorScheme === "light" ? "#f1f4f6" : theme.colors.dark[7],
    fontWeight: 700,
    fontSize: "13px",
    color: theme.colors.gray[7],
  },
  rowCell: {
    paddingTop: "16px",
    paddingBottom: "16px",
    verticalAlign: "middle",
  },
  smallBadge: {
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 500,
  },
  employeeInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  roleText: {
    fontSize: "12px",
    color: theme.colors.gray[6],
  },
  productionBadge: {
    fontWeight: 600,
    fontSize: "12px",
  },
  actionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  overtimeButton: {
    fontSize: "11px",
    padding: "4px 8px",
    height: "auto",
  },
  imageIcon: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
      color: theme.colors.blue[6],
    },
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: theme.spacing.md,
  },
  imageContainer: {
    borderRadius: theme.radius.md,
    overflow: "hidden",
    border: `1px solid ${theme.colors.gray[3]}`,
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },
  imageCountBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 2,
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 600,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "white",
  },
}));

type Session = {
  sessionId: string;
  punchIn: string;
  suggestedPunchOut?: string;
};

export default function AdminAttendanceTable() {
  const { classes } = useStyles();
  const { mutate: approveOvertime, isLoading: isApproving } =
    useUpdateOvertimeStatus();

  const { user } = useUser();

  const roleNormalized = (user?.role ?? "").toString();
  const isAdmin = /admin/i.test(roleNormalized);
  const isSupervisor = /supervisor/i.test(roleNormalized);
  const isDirector = /director/i.test(roleNormalized);
  const isMember = /member/i.test(roleNormalized);
  const isRestrictedRole = isSupervisor || isDirector;

  const { data: deptData, isLoading: isLoadingDepartments } =
    useGetDepartments();
  const { data: programsData, isLoading: isLoadingPrograms } = useGetPrograms();

  const deptArray: any[] = deptData?.data ?? [];
  const programsArray: any[] = programsData?.data ?? [];

  // --- State (MUST be before useMemo that uses them) ---
  const [page, setPage] = useState(1);
  const [selectedType, setSelectedType] = useState<RangeOption>("Today");
  const [statusFilter, setStatusFilter] = useState<string | null>("Present");
  const [deptFilter, setDeptFilter] = useState<string | null>(null); // Will be set in useEffect
  const [programFilter, setProgramFilter] = useState<string | null>(null); // Will be set in useEffect
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [dateSortOrder, setDateSortOrder] = useState<"asc" | "desc">("desc");
  const [rowsPerPage] = useState<number>(10);
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedRange, setSelectedRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );
  const [imagesModalOpened, setImagesModalOpened] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>("");
  const [deleteModalOpened, setDeleteModalOpened] = useState({
    isOpen: false,
    recordId: "",
    employeeName: "",
    day: "",
  });
  const [editModalOpened, setEditModalOpened] = useState<{
    isOpen: boolean;
    recordId: string;
    employeeName: string;
    day: string;
    checkIn: string | null;
    checkOut: string | null;
    status: "Present" | "Absent";
    isHalfDay: boolean;
    notes: string;
  }>({
    isOpen: false,
    recordId: "",
    employeeName: "",
    day: "",
    checkIn: null,
    checkOut: null,
    status: "Present",
    isHalfDay: false,
    notes: "",
  });

  const deptOptions = useMemo(() => {
    if (!Array.isArray(deptArray)) return [{ value: "All", label: "All" }];

    let filteredDepts = deptArray;

    // For directors, only show departments they manage
    if (isDirector && user?._id) {
      filteredDepts = deptArray.filter((d) => {
        // Show departments where user is director
        const isDirectorOfDept =
          d.director?._id?.toString() === user._id?.toString() ||
          d.director === user._id;
        // Also show user's own department
        const isOwnDept =
          d._id?.toString() === user.department?._id?.toString() ||
          d._id === user.department?._id;
        return isDirectorOfDept || isOwnDept;
      });
    }

    let baseOpts = filteredDepts.map((d) => ({
      value: d._id,
      label: d.name,
    }));

    // Ensure the currently selected department is in the list
    if (isDirector && deptFilter && deptFilter !== "All") {
      const isCurrentDeptInList = baseOpts.some(
        (opt) => opt.value?.toString() === deptFilter?.toString(),
      );
      if (!isCurrentDeptInList) {
        // Find the dept in the full array and add it
        const currentDept = deptArray.find(
          (d) => d._id?.toString() === deptFilter?.toString(),
        );
        if (currentDept) {
          baseOpts = [
            {
              value: currentDept._id,
              label: currentDept.name,
            },
            ...baseOpts,
          ];
        }
      }
    }

    // For directors/supervisors, don't show "All" option
    if (isRestrictedRole) {
      return baseOpts;
    }

    return [{ value: "All", label: "All" }, ...baseOpts];
  }, [deptArray, isDirector, isRestrictedRole, user, deptFilter]);

  const programOptions = useMemo(() => {
    if (!Array.isArray(programsArray)) return [{ value: "All", label: "All" }];

    let filteredPrograms = programsArray;

    // For supervisors, only show programs they manage
    if (isSupervisor && user?._id) {
      filteredPrograms = programsArray.filter((p) => {
        return (
          p.supervisor?._id?.toString() === user._id?.toString() ||
          p.supervisor === user._id
        );
      });
    }

    let baseOpts = filteredPrograms.map((p) => ({
      value: p._id,
      label: p.name,
    }));

    // Ensure the currently selected program is in the list
    if (isSupervisor && programFilter && programFilter !== "All") {
      const isCurrentProgInList = baseOpts.some(
        (opt) => opt.value?.toString() === programFilter?.toString(),
      );
      if (!isCurrentProgInList) {
        // Find the program in the full array and add it
        const currentProg = programsArray.find(
          (p) => p._id?.toString() === programFilter?.toString(),
        );
        if (currentProg) {
          baseOpts = [
            {
              value: currentProg._id,
              label: currentProg.name,
            },
            ...baseOpts,
          ];
        }
      }
    }

    // For supervisors, don't show "All" option
    if (isSupervisor) {
      return baseOpts;
    }

    return [{ value: "All", label: "All" }, ...baseOpts];
  }, [programsArray, isSupervisor, user, programFilter]);

  // --- Debounce search query ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!user) return;
    // Set default department to user's own department on first load
    // This prevents showing the logged-in user's data by default
    if (deptFilter === null && user.department?._id) {
      setDeptFilter(user.department._id as string);
    }
  }, [user, deptFilter]);

  const { data: unclosedData, refetch } = useGetUnclosedSessions(
    selectedEmployeeId!,
  );

  console.log("📅 Selected Range:", selectedRange);

  // --- API Hook (paginated) ---
  const {
    data,
    isLoading,
    refetch: refetchAttendanceList,
  } = useGetAttendanceAdminList(
    page,
    rowsPerPage,
    statusFilter,
    "last7days",
    // pass null to API when "All" is selected
    deptFilter === "All" ? null : deptFilter,
    selectedRange.start,
    selectedRange.end,
    debouncedSearchQuery,
    programFilter === "All" ? null : programFilter,
    dateSortOrder,
  );

  const records = data?.data?.records ?? [];
  const pagination = data?.data?.pagination;

  // --- Handle Date Range Change ---
  const handleDateChange = (range: DateRange) => {
    setSelectedRange(range);
    setPage(1);
  };

  // --- Helper for production hours badge color ---
  const getProductionColor = (hours: number | string) => {
    const val = parseFloat(hours as string);
    if (val >= 8) return "green";
    if (val < 7.5) return "red";
    return "orange";
  };

  const handleCheckout = async (id: string) => {
    setSelectedEmployeeId(id);
    const { data } = await refetch();

    if (data?.data && data.data.length > 0) {
      setSessions(data.data);
      setOpen(true);
    } else {
      setSessions([]);
      setOpen(false);
    }
  };

  // --- Handle Overtime Approval ---
  const handleApproveOvertime = (employeeId: string, recordId: string) => {
    approveOvertime(
      {
        recordId: recordId,
        isOvertimeApproved: true,
      },
      {
        onSuccess: () => {
          // Refresh the attendance list after successful approval
          refetchAttendanceList();
        },
      },
    );
  };

  // --- Handle Image Icon Click ---
  const handleImageClick = (images: string[], employeeName: string) => {
    if (images && images.length > 0) {
      setSelectedImages(images);
      setSelectedEmployeeName(employeeName);
      setImagesModalOpened(true);
    }
  };

  // --- Render Image Icon or Dash ---
  const renderImageCell = (images: string[], employeeName: string) => {
    if (!images || images.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          -
        </Text>
      );
    }

    return (
      <Tooltip
        label={`View ${images.length} image${images.length > 1 ? "s" : ""}`}
        position="top"
      >
        <ActionIcon
          className={classes.imageIcon}
          color="blue"
          onClick={() => handleImageClick(images, employeeName)}
        >
          <IconPhoto size={20} />
          {images.length > 1 && (
            <Text
              size="xs"
              fw={700}
              style={{ position: "absolute", top: -8, right: -8 }}
            >
              {images.length}
            </Text>
          )}
        </ActionIcon>
      </Tooltip>
    );
  };

  if (isLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Loader />
      </Center>
    );
  }

  return (
    <>
      <Card className={classes.containerCard}>
        {/* Header */}
        <div className={classes.headerRow}>
          <AdminAttendanceDetail />

          {/* All filters in one line */}
          <div className={classes.filtersContainer}>
            {/* Date Range */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Date Range</Text>
              <DateRangeSelector
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                onDateRangeChange={handleDateChange}
                selectedRange={selectedRange}
              />
            </div>

            {/* Status Filter */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Status</Text>
              <Select
                data={["Present", "Absent", "Leave"]}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setPage(1);
                }}
                placeholder="Select Status"
                sx={{ width: 140 }}
              />
            </div>

            {/* Department Filter */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Department</Text>
              <Select
                data={deptOptions}
                value={
                  deptFilter ||
                  (deptOptions.length > 0 ? deptOptions[0]?.value : null) ||
                  ""
                }
                onChange={(val) => {
                  // Only allow supervisors/members to be prevented from changing department
                  // Directors can change between their managed departments
                  if (isSupervisor || isMember) return;
                  setDeptFilter(val);
                  setPage(1);
                }}
                placeholder="Select Department"
                disabled={isSupervisor || isMember || isLoadingDepartments}
                clearable={!isRestrictedRole}
                sx={{ width: 140 }}
              />
            </div>

            {/* Program Filter */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Program</Text>
              <Select
                data={programOptions}
                value={
                  programFilter ||
                  (programOptions.length > 0
                    ? programOptions[0]?.value
                    : null) ||
                  ""
                }
                onChange={(val) => {
                  setProgramFilter(val);
                  setPage(1);
                }}
                placeholder="Select Program"
                disabled={isLoadingPrograms}
                clearable
                sx={{ width: 140 }}
              />
            </div>

            {/* Search Input */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Employee Name</Text>
              <TextInput
                placeholder="Search by name, email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.currentTarget.value);
                }}
                sx={{ width: 200 }}
              />
            </div>

            {/* Date Sort */}
            <div className={classes.filterWrapper}>
              <Text className={classes.filterLabel}>Date Sort</Text>
              <SegmentedControl
                value={dateSortOrder}
                onChange={(val) => setDateSortOrder(val as "asc" | "desc")}
                data={[
                  { label: "Newest", value: "desc" },
                  { label: "Oldest", value: "asc" },
                ]}
                size="sm"
              />
            </div>
          </div>
        </div>

        <Divider mb="md" />

        {/* Table */}
        <ScrollArea>
          <Table highlightOnHover verticalSpacing="sm">
            <thead>
              <tr>
                <th className={classes.tableHeader}>Employee</th>
                <th className={classes.tableHeader}>Status</th>
                <th className={classes.tableHeader}>Department</th>
                <th className={classes.tableHeader}>Date</th>
                <th className={classes.tableHeader}>Clock In</th>
                <th className={classes.tableHeader}>Clock Out</th>
                <th className={classes.tableHeader}>Location</th>
                <th className={classes.tableHeader}>Break</th>
                <th className={classes.tableHeader}>Late</th>
                <th className={classes.tableHeader}>Production Hours</th>
                <th className={classes.tableHeader}>Images</th>
                <th className={classes.tableHeader}>Overtime</th>
                <th className={classes.tableHeader}>Overtime Status</th>
                <th className={classes.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {records?.length > 0 ? (
                records.map((row: any) => (
                  <tr key={row?.recordId}>
                    <td className={classes.rowCell}>
                      <div className={classes.employeeInfo}>
                        <Avatar
                          src={row?.employee?.profileImage}
                          radius="xl"
                          size={36}
                        />
                        <div>
                          <Text size="sm" fw={500}>
                            {row?.employee?.name}
                          </Text>
                          <Text className={classes.roleText}>
                            {row?.employee?.position}
                          </Text>
                        </div>
                      </div>
                    </td>

                    <td className={classes.rowCell}>
                      <Badge
                        color={
                          row?.status === "Present"
                            ? "green"
                            : row?.status === "Leave"
                              ? "blue"
                              : "red"
                        }
                        variant="light"
                        className={classes.smallBadge}
                      >
                        {row?.status}
                      </Badge>
                    </td>

                    <td className={classes.rowCell}>
                      <Text size="sm">
                        {row?.employee?.department?.name ?? "-"}
                      </Text>
                    </td>

                    <td className={classes.rowCell}>
                      <Text size="sm">
                        {formatDateToDDMMYYYY(row.day ? row.day : "")}
                      </Text>
                    </td>
                    <td className={classes.rowCell}>
                      <Text size="sm">
                        {row?.checkIn ? formatToLocalTime(row?.checkIn) : "-"}
                      </Text>
                    </td>

                    <td className={classes.rowCell}>
                      <Text size="sm">
                        {row?.checkOut ? formatToLocalTime(row?.checkOut) : "-"}
                      </Text>
                    </td>

                    <td className={classes.rowCell}>
                      {row?.location?.lat && row?.location?.lng ? (
                        <Tooltip
                          label={
                            row?.locationClockinNotes ||
                            row?.locationClockoutNotes ||
                            "No location notes"
                          }
                          position="top"
                        >
                          <Text
                            component="a"
                            size="sm"
                            style={{
                              cursor: "pointer",
                              color: "#1a73e8",
                              textDecoration: "underline",
                            }}
                            onClick={() => {
                              const mapsUrl = `https://www.google.com/maps?q=${row?.location?.lat},${row?.location?.lng}`;
                              window.open(mapsUrl, "_blank");
                            }}
                          >
                            {row?.location?.lat?.toFixed(4)},{" "}
                            {row?.location?.lng?.toFixed(4)}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">
                          -
                        </Text>
                      )}
                    </td>

                    <td className={classes.rowCell}>
                      <Text size="sm">
                        {row?.breakMinutes && Number(row.breakMinutes) > 0
                          ? row.breakMinutes
                          : "-"}
                      </Text>
                    </td>

                    <td className={classes.rowCell}>
                      {row.isLate ? (
                        (() => {
                          // Calculate formatted late message
                          let lateMessage = row.notes || "No notes available";

                          // Check if notes contains minutes information
                          if (row.notes && row.notes.includes("Late by")) {
                            const minutesMatch = row.notes.match(/(\d+)/);
                            if (minutesMatch && minutesMatch[1]) {
                              const totalMinutes = parseInt(
                                minutesMatch[1],
                                10,
                              );
                              const hours = Math.floor(totalMinutes / 60);
                              const minutes = totalMinutes % 60;

                              if (hours > 0 && minutes > 0) {
                                lateMessage = `Late by ${hours} hour${hours > 1 ? "s" : ""} and ${minutes} minute${minutes > 1 ? "s" : ""}`;
                              } else if (hours > 0) {
                                lateMessage = `Late by ${hours} hour${hours > 1 ? "s" : ""}`;
                              } else {
                                lateMessage = `Late by ${minutes} minute${minutes > 1 ? "s" : ""}`;
                              }
                            }
                          }

                          return (
                            <Tooltip label={lateMessage} position="top">
                              <Text
                                size="sm"
                                c="green"
                                fw={500}
                                style={{ cursor: "pointer" }}
                              >
                                Yes
                              </Text>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <Text size="sm" c="red" fw={500}>
                          No
                        </Text>
                      )}
                    </td>

                    <td className={classes.rowCell}>
                      <Badge
                        color={getProductionColor(row.productionHours)}
                        variant="filled"
                        className={classes.productionBadge}
                      >
                        {row?.productionHours ?? "-"}
                      </Badge>
                    </td>

                    {/* Images Column */}
                    <td className={classes.rowCell}>
                      {renderImageCell(row?.images || [], row?.employee?.name)}
                    </td>

                    <td className={classes.rowCell}>
                      <Text size="sm">{row.overtimeMinutes ?? "-"}</Text>
                    </td>
                    <td className={classes.rowCell}>
                      {row?.isOvertime ? (
                        <Badge
                          color={row?.isOvertimeApproved ? "teal" : "orange"}
                          variant="light"
                          className={classes.smallBadge}
                        >
                          {row?.isOvertimeApproved ? "Approved" : "Pending"}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          -
                        </Text>
                      )}
                    </td>

                    <td className={classes.rowCell}>
                      <div className={classes.actionContainer}>
                        <Group spacing="xs" noWrap>
                          {/* Open session: still allow checkout */}
                          {row?.checkIn && !row?.checkOut && (
                            <Tooltip label="Click to checkout" position="top">
                              <Button
                                variant="filled"
                                radius="md"
                                size="sm"
                                onClick={() =>
                                  handleCheckout(row?.employee?.id)
                                }
                              >
                                Checkout
                              </Button>
                            </Tooltip>
                          )}

                          {/* Closed session: keep overtime approval UI */}
                          {!(row?.checkIn && !row?.checkOut) &&
                            (row?.isOvertime && !row?.isOvertimeApproved ? (
                              <Tooltip label="Approve overtime" position="top">
                                <Button
                                  variant="outline"
                                  loading={isApproving}
                                  disabled={isApproving}
                                  color="orange"
                                  size="xs"
                                  className={classes.overtimeButton}
                                  onClick={() =>
                                    handleApproveOvertime(
                                      row?.employee?.id,
                                      row?.recordId,
                                    )
                                  }
                                >
                                  Approve Overtime
                                </Button>
                              </Tooltip>
                            ) : (
                              <Text size="sm" c="dimmed">
                                Completed
                              </Text>
                            ))}

                          {/* Allow edit/delete even for open sessions, but still block Leave/Holiday */}
                          {row?.status !== "Leave" &&
                            row?.status !== "Holiday" && (
                              <>
                                <Tooltip
                                  label="Edit attendance record"
                                  position="top"
                                >
                                  <ActionIcon
                                    variant="transparent"
                                    onClick={() =>
                                      setEditModalOpened({
                                        isOpen: true,
                                        recordId: row?.recordId,
                                        employeeName: row?.employee?.name || "",
                                        day: row?.day || "",
                                        checkIn: row?.checkIn || null,
                                        checkOut: row?.checkOut || null,
                                        status:
                                          row?.status === "Absent"
                                            ? "Absent"
                                            : "Present",
                                        isHalfDay: row?.isHalfDay ?? false,
                                        notes: row?.notes || "",
                                      })
                                    }
                                  >
                                    <IconPencil size={16} color="blue" />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip
                                  label="Delete attendance record"
                                  position="top"
                                >
                                  <ActionIcon
                                    variant="transparent"
                                    onClick={() =>
                                      setDeleteModalOpened({
                                        isOpen: true,
                                        recordId: row?.recordId,
                                        employeeName: row?.employee?.name || "",
                                        day: row?.day || "",
                                      })
                                    }
                                  >
                                    <IconTrash size={16} color="red" />
                                  </ActionIcon>
                                </Tooltip>
                              </>
                            )}
                        </Group>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={14}>
                    <Center py="md">
                      <Text color="dimmed">No records found</Text>
                    </Center>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>

        {/* Pagination Footer */}
        {pagination && (
          <Flex justify="space-between" align="center" mt="md">
            <Text size="sm" color="dimmed">
              Showing page {pagination.currentPage} of {pagination.totalPages} —{" "}
              {pagination.totalRecords} records
            </Text>
            <Center>
              <Pagination
                total={pagination.totalPages}
                page={page}
                onChange={setPage}
                size="sm"
                siblings={1}
                boundaries={1}
              />
            </Center>
          </Flex>
        )}
        <UnclosedSessionModal
          opened={open}
          sessions={sessions}
          onResolved={() => {
            setOpen(false);
          }}
        />
      </Card>

      {/* Images Modal */}
      <Modal
        opened={imagesModalOpened}
        onClose={() => setImagesModalOpened(false)}
        size="xl"
        centered
        // overlayProps={{
        //   opacity: 0.55,
        //   blur: 3,
        // }}
      >
        {selectedImages.length === 0 ? (
          <Center py="xl">
            <Text color="dimmed">No images available</Text>
          </Center>
        ) : (
          <>
            <Text size="sm" color="dimmed" mb="md">
              Total {selectedImages.length} image
              {selectedImages.length > 1 ? "s" : ""} captured
            </Text>
            <Grid gutter="md">
              {selectedImages.map((imageUrl, index) => (
                <Grid.Col key={index} span={6}>
                  <div className={classes.imageContainer}>
                    <Image
                      src={imageUrl}
                      alt={`Image ${index + 1}`}
                      height={200}
                      fit="cover"
                      withPlaceholder
                    />
                  </div>
                </Grid.Col>
              ))}
            </Grid>
          </>
        )}
      </Modal>

      {/* Delete Attendance Modal */}
      <DeleteAttendanceModal
        opened={deleteModalOpened.isOpen}
        onClose={() =>
          setDeleteModalOpened({
            isOpen: false,
            recordId: "",
            employeeName: "",
            day: "",
          })
        }
        recordId={deleteModalOpened.recordId}
        employeeName={deleteModalOpened.employeeName}
        day={deleteModalOpened.day}
      />

      {/* Edit Attendance Modal */}
      <EditAttendanceModal
        opened={editModalOpened.isOpen}
        onClose={() =>
          setEditModalOpened({
            isOpen: false,
            recordId: "",
            employeeName: "",
            day: "",
            checkIn: null,
            checkOut: null,
            status: "Present",
            isHalfDay: false,
            notes: "",
          })
        }
        recordId={editModalOpened.recordId}
        employeeName={editModalOpened.employeeName}
        day={editModalOpened.day}
        initialCheckIn={editModalOpened.checkIn}
        initialCheckOut={editModalOpened.checkOut}
        initialStatus={editModalOpened.status}
        initialIsHalfDay={editModalOpened.isHalfDay}
        initialNotes={editModalOpened.notes}
      />
    </>
  );
}
